import { message, Modal, Table, TableColumnsType } from 'antd'
import React, { useState } from 'react'
import dayjs from 'dayjs'
import { Box, IconButton, Link, Typography } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'

import { ONLY_DATE_FORMAT, ONLY_TIME_FORMAT } from '../../../constants/global'
import { axiosInstance } from '../../../hooks/useAxios'
import { IExpense, IExpenseProduct } from '../../../types/expense'
import { ImageLinks, LogoTypeSocial } from '../../../constants/image-link'
import { ExpenseFormModal } from './ExpenseFormModal'
import { IFilterMapped } from './ExpensesFilter'
import CircularLoading from '../../../components/CircularProgress'
import ImgTag from '../../../components/ImgTag'
import Pagination from '../../../components/Pagination'
import ProductItemTag from '../../../components/ProductItemTag'
import { ERRORS_MESSAGE_MAPPER } from '../../../constants/errors'
import { Tag } from '../../../components/Tag'

interface IExpensesTableProps {
  isLoading: boolean
  data: IExpense[]
  onUpdateExpensesSuccess: () => void
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  onPageChange: (filter: IFilterMapped) => void
  total: number
}

const ExpensesTable = (props: IExpensesTableProps) => {
  const {
    isLoading,
    data,
    onUpdateExpensesSuccess,
    filter,
    onFilterChange,
    onPageChange,
    total,
  } = props
  const [isModalEditExpense, setIsModalEditExpense] = useState<boolean>(false)
  const [expenseEdit, setExpenseEdit] = useState<IExpense>()
  const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false)

  const onHandleDeleteExpense = async (expense: IExpense) => {
    Modal.confirm({
      title: 'ยืนยันการลบรายการค่าใช้จ่าย',
      content: `ยืนยันการลบรายการค่าใช้จ่าย ${expense.name}`,
      onOk: async () => {
        setIsLoadingDelete(true)
        try {
          await axiosInstance.delete(`/costs/${expense.objectId}`)
          message.success(`ลบรายการค่าใช้จ่าย ${expense.name} สำเร็จ`)
          onUpdateExpensesSuccess()
        } catch (e: any) {
          const messageError =
            ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
            'บันทึกข้อมูลไม่สำเร็จ'
          message.error(messageError)
        }
        setIsLoadingDelete(false)
      },
      onCancel: () => {
        setIsLoadingDelete(false)
      },
    })
  }

  const columns: TableColumnsType<IExpense & { key: number }> = [
    {
      title: 'ลำดับ',
      dataIndex: 'numberRow',
      key: 'numberRow',
      align: 'center' as const,
      width: 80,
      render: (numberRow: number) => {
        return (
          <Box>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              textAlign={'center'}
            >
              {numberRow}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'หัวข้อ',
      dataIndex: 'name',
      key: 'name',
      align: 'left' as const,
      width: 200,
      render: (name: string) => {
        return (
          <Box>
            <Typography variant="subtitle1" color="text.secondary">
              {name}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'ช่องทาง',
      dataIndex: 'name',
      key: 'name',
      align: 'center' as const,
      render: (name: string, expense: IExpense) => {
        return (
          <Box
            sx={{
              minWidth: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              textAlign: 'left',
            }}
          >
            {expense?.distribution ? (
              <>
                <ImgTag
                  imageUrl={
                    ImageLinks?.[
                      `LOGO_${
                        expense?.distribution?.type?.toUpperCase() ?? ''
                      }` as LogoTypeSocial
                    ]
                  }
                  name={name}
                  size={50}
                />
                <Box ml={1}>
                  <Typography variant="h6" color="text.secondary">
                    {name}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    {expense.distribution.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    <Link
                      href={expense.distribution.url}
                      target="_blank"
                      underline="hover"
                    >
                      {expense.distribution.url}
                    </Link>
                  </Typography>
                </Box>
              </>
            ) : (
              <>ไม่ระบุ</>
            )}
          </Box>
        )
      },
    },
    {
      title: 'สินค้า',
      dataIndex: 'products',
      key: 'products',
      align: 'center' as const,
      render: (products: IExpenseProduct[]) => {
        return (
          <Box minWidth={150}>
            {products?.map((item: IExpenseProduct, index: number) => {
              return (
                <ProductItemTag
                  key={index}
                  item={{
                    url: item.image,
                    name: item.name,
                  }}
                />
              )
            })}
          </Box>
        )
      },
    },
    {
      title: 'สถานะของการยิง Ads',
      dataIndex: 'adsStatus',
      key: 'adsStatus',
      align: 'center' as const,
      width: 120,
      render: (adsStatus: string) => (
        <Typography variant="subtitle2" color="text.secondary">
          {adsStatus ? (
            adsStatus === 'active' ? (
              <Tag color="success">ยิงติด</Tag>
            ) : (
              <Tag color="error">ยิงไม่ติด</Tag>
            )
          ) : (
            '-'
          )}
        </Typography>
      ),
    },
    {
      title: 'กำหนดให้เป็นค่าขนส่ง',
      dataIndex: 'isShipping',
      key: 'isShipping',
      align: 'center' as const,
      width: 120,
      render: (isShipping: boolean) => (
        <Typography variant="subtitle2" color="text.secondary">
          {isShipping ? <Tag color="success">ใช่</Tag> : ''}
        </Typography>
      ),
    },
    {
      title: 'ราคา (บาท)',
      dataIndex: 'amount',
      key: 'amount',
      align: 'center' as const,
      render: (amount: number) => {
        return (
          <Box sx={{ minWidth: 120 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {amount.toLocaleString()}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'หมายเหตุ',
      dataIndex: 'note',
      key: 'note',
      align: 'center' as const,
      render: (note: string) => {
        return (
          <Box sx={{ minWidth: 180 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {note?.length > 0 ? note : <em>ไม่ระบุ</em>}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'วันที่ลงค่าใช้จ่าย',
      dataIndex: 'costDate',
      key: 'costDate',
      width: 160,
      align: 'center' as const,
      render: (costDate: number) => {
        return (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {dayjs(costDate).format(ONLY_DATE_FORMAT)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(costDate).format(ONLY_TIME_FORMAT)}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'วันที่เพิ่ม',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      align: 'center' as const,
      render: (createdAt: string) => {
        return (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {dayjs(createdAt).format(ONLY_DATE_FORMAT)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(createdAt).format(ONLY_TIME_FORMAT)}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'วันที่แก้ไข',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 120,
      align: 'center' as const,
      render: (updatedAt: string) => {
        return (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {dayjs(updatedAt).format(ONLY_DATE_FORMAT)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(updatedAt).format(ONLY_TIME_FORMAT)}
            </Typography>
          </Box>
        )
      },
    },
    {
      dataIndex: '',
      key: 'action',
      width: 120,
      align: 'center' as const,
      render: (_: undefined, expense: IExpense) => {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {/* <IconButton onClick={() => {
              setExpenseEdit(expense)
              setIsModalEditExpense(true)
            }}>
              <EditIcon />
            </IconButton> */}
            <IconButton onClick={() => onHandleDeleteExpense(expense)}>
              <DeleteIcon />
            </IconButton>
          </Box>
        )
      },
    },
  ]

  return (
    <>
      <ExpenseFormModal
        isOpen={isModalEditExpense}
        onUpdateExpensesSuccess={onUpdateExpensesSuccess}
        onClose={(visible) => setIsModalEditExpense(visible)}
        expense={expenseEdit}
      />
      <CircularLoading isLoading={isLoading || isLoadingDelete} />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) => onPageChange({ ...filter, page, perPage })}
        mb={2}
      />
      <Table
        size={'middle'}
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={data?.map((expense: IExpense, index: number) => {
          return {
            ...expense,
            key: index,
            numberRow:
              index + 1 + ((filter.page || 0) - 1) * (filter.perPage || 0),
          }
        })}
        bordered
        pagination={false}
      />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) => onPageChange({ ...filter, page, perPage })}
      />
    </>
  )
}

export default ExpensesTable
